import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Barcode from 'react-barcode';
import { QRCodeSVG } from 'qrcode.react';
import { Phone, MapPin, User, Package, Weight, Calendar, Hash, Printer, X } from 'lucide-react';
import { api } from '@/lib/api';

export default function ShipmentPrintPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [shipment, setShipment] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchShipment();
  }, [id]);

  const fetchShipment = async () => {
    try {
      const response = await api.get(`/api/shipments/${id}`);
      if (response.data?.success !== false) {
        const payload = response.data?.data ?? response.data;
        const raw = payload?.shipment ?? payload;
        const normalized = {
          ...raw,
          trackingNumber: raw?.trackingNumber || raw?.policyNumber || '',
          senderName: raw?.senderName || raw?.senderCustomer?.name || raw?.createdByUser?.fullName || '',
          senderPhone: raw?.senderPhone || raw?.senderCustomer?.phone || raw?.createdByUser?.phone || '',
          senderAddress: raw?.senderAddress || raw?.senderCustomer?.address || '',
          senderCity: raw?.senderCity || raw?.senderCustomer?.city || '',
          receiverName: raw?.receiverName || raw?.recipientName || '',
          receiverPhone: raw?.receiverPhone || raw?.recipientPhone || '',
          receiverAddress: raw?.receiverAddress || '',
          receiverCity: raw?.receiverCity || '',
          packageDescription: raw?.packageDescription || raw?.description || raw?.contentType || '',
          weight: raw?.weight ?? raw?.packageWeight ?? 0,
          codAmount: raw?.codAmount ?? raw?.totalAmount ?? 0,
          statusNameAr: raw?.statusNameAr || raw?.status?.nameAr || '',
          statusName: raw?.statusName || raw?.status?.name || '',
          createdAt: raw?.createdAt || raw?.created_at || null,
        };
        setShipment(normalized);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div></div>;
  if (!shipment) return <div className="text-center py-20"><p className="text-red-600 text-xl">الشحنة غير موجودة</p><button onClick={() => navigate('/shipments')} className="mt-4 bg-gray-600 text-white px-6 py-2 rounded">العودة</button></div>;

  // هذا هو الرابط الذي سيُشفر داخل الـ QR Code
  const trackingUrl = `https://targetexpress.secretofthedesert.com/track/${shipment.trackingNumber}`;
  const shipmentNotes = shipment.notes || shipment.deliveryNotes || shipment.deliveryNotes || '';

  return (
    <div className="min-h-screen bg-gray-100 p-4" dir="rtl">
      <div className="no-print fixed top-4 left-4 flex gap-2 z-50">
        <button onClick={() => window.print()} className="bg-orange-600 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 shadow-lg">
          <Printer size={20} /> طباعة البوليصة
        </button>
        <button onClick={() => navigate('/shipments')} className="bg-gray-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 shadow-lg">
          <X size={20} /> إغلاق
        </button>
      </div>

      <div className="awb-label max-w-[210mm] mx-auto bg-white shadow-xl p-8 print:shadow-none print:p-4 print:max-w-full">
        
        {/* Header */}
        <div className="border-b-4 border-blue-900 pb-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo-light.png" alt="Target Express" className="w-[136px] h-[136px] object-contain" />
            <div>
              <h1 className="text-2xl font-black text-blue-900">Target Express</h1>
              <p className="text-orange-600 font-bold text-xs">Shipping & Logistics Solutions</p>
            </div>
          </div>
          <div className="text-left">
            <div className="text-sm text-gray-500 mb-2 font-bold">رقم التتبع | Tracking #</div>
            <div className="bg-blue-50 border-2 border-blue-900 rounded-lg px-6 py-3">
              <span className="text-xl font-black text-blue-900 tracking-wider">{shipment.trackingNumber}</span>
            </div>
          </div>
        </div>

        {/* Barcodes Section */}
        <div className="border-b-2 border-gray-200 pb-6 mb-6">
          <div className="flex items-center justify-between gap-6">
            {/* Barcode */}
            <div className="flex-1 text-center bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="text-xs text-gray-500 mb-2 font-bold">باركود التتبع (CODE128)</div>
              {shipment.trackingNumber && (
                <div className="flex justify-center">
                  <Barcode 
                    value={shipment.trackingNumber} 
                    height={55} 
                    width={1.4} 
                    fontSize={10} 
                    displayValue={false} 
                    format="CODE128"
                  />
                </div>
              )}
            </div>
            
            {/* QR Code - مضمون 100% */}
            <div className="text-center bg-gray-50 p-4 rounded-lg border border-gray-200 border-r-2 border-r-gray-300">
              <div className="text-xs text-gray-500 mb-2 font-bold">امسح للتتبع (QR)</div>
              <div className="flex justify-center">
                <QRCodeSVG 
                  value={trackingUrl} 
                  size={85} 
                  level="H" 
                  includeMargin={true}
                />
              </div>

            </div>
          </div>
        </div>

        {/* Receiver & Sender */}
        <div className="grid grid-cols-2 gap-3 mb-3 border-b border-gray-200 pb-3">
          <div className="bg-orange-50 p-3 rounded-lg border border-orange-100">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center">
                <User className="text-white" size={18} />
              </div>
              <span className="text-sm font-black text-orange-700">المستلم | TO</span>
            </div>
            <div className="text-base font-black text-gray-900 mb-1 leading-tight">{shipment.receiverName}</div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Phone size={18} className="text-orange-600" />
                <span className="font-mono font-bold text-sm text-gray-900">{shipment.receiverPhone || 'غير محدد'}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={18} className="text-orange-600" />
                <span className="font-bold text-sm">{shipment.receiverCity || 'غير محدد'}</span>
              </div>
              <div className="text-gray-700 font-medium">{shipment.receiverAddress || 'غير محدد'}</div>
            </div>
          </div>

          <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-blue-900 rounded-full flex items-center justify-center">
                <User className="text-white" size={18} />
              </div>
              <span className="text-sm font-black text-blue-900">المرسل | FROM</span>
            </div>
            <div className="text-base font-black text-gray-900 mb-1 leading-tight">{shipment.senderName || 'Target Express'}</div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Phone size={18} className="text-blue-900" />
                <span className="font-mono font-bold text-sm">{shipment.senderPhone || 'غير محدد'}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={18} className="text-blue-900" />
                <span className="font-bold text-sm">{shipment.senderCity || 'غير محدد'}</span>
              </div>
              <div className="text-gray-700 font-medium">{shipment.senderAddress || 'غير محدد'}</div>
            </div>
          </div>
        </div>
{/* Shipment Contents */}
<div className="mb-3 border-b border-gray-200 pb-3">
  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
    <div className="text-xs text-orange-600 font-black mb-1">
      محتوى الشحنة
    </div>

    <div className="text-base font-black text-gray-900">
      {shipment.packageDescription
        || shipment.description
        || shipment.contentType
        || 'غير محدد'}
    </div>
  </div>
</div>

        {/* Shipment Summary */}
        <div className="grid grid-cols-3 gap-2 mb-3 border-b border-gray-200 pb-3">
          <div className="text-center p-2 bg-gray-50 rounded-lg border border-gray-100">
            <Weight size={24} className="mx-auto text-blue-900 mb-1" />
            <div className="text-xs text-gray-500 font-bold">الوزن</div>
            <div className="text-lg font-black text-gray-900">{shipment.weight || 0} <span className="text-xs font-normal">كجم</span></div>
          </div>

          <div className="text-center p-2 bg-amber-50 rounded-lg border border-amber-100">
            <div className="text-xs text-gray-500 font-bold mb-1">التحصيل COD</div>
            <div className="text-lg font-black text-orange-600">
              {shipment.codAmount || 0} <span className="text-xs font-normal">ج.م</span>
            </div>
          </div>

          <div className="text-center p-2 bg-green-50 rounded-lg border border-green-100">
            <Package size={24} className="mx-auto text-green-600 mb-1" />
            <div className="text-xs text-gray-500 font-bold">الحالة</div>
<div className={`text-base font-black ${
  shipment.statusId === 2
    ? 'text-green-600'
    : shipment.statusId === 3
    ? 'text-blue-600'
    : shipment.statusId === 4
    ? 'text-purple-600'
    : shipment.statusId === 5 || shipment.statusId === 6
    ? 'text-red-600'
    : 'text-amber-600'
}`}>
  {(() => {
    const statusMap: Record<number, string> = {
      1: 'جديد / قيد الانتظار',
      2: 'تم التسليم',
      3: 'في الطريق',
      4: 'في الفرع',
      5: 'مرتجع / فشل',
      6: 'مرتجع / فشل',
    };

    return statusMap[shipment.statusId]
      || shipment.statusNameAr
      || shipment.status?.nameAr
      || shipment.statusName
      || shipment.status?.name
      || 'غير محدد';
  })()}
</div>
          </div>
        </div>

        {/* Footer Info */}
        <div className="grid grid-cols-2 gap-6 text-sm mb-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Hash size={16} className="text-gray-500" />
              <span className="text-gray-600">رقم الشحنة الداخلي:</span>
              <span className="font-mono font-black text-gray-900">{shipment.id}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-gray-500" />
              <span className="text-gray-600">تاريخ الإنشاء:</span>
              <span className="font-bold text-gray-900">{shipment.createdAt ? new Date(shipment.createdAt).toLocaleDateString('ar-EG') : 'غير محدد'}</span>
            </div>
          </div>
          {shipmentNotes && (
            <div className="bg-orange-50 p-4 rounded-xl border-2 border-orange-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-black text-orange-700">ملاحظات الشحنة</span>
              </div>
              <p className="text-orange-900 font-bold whitespace-pre-wrap">{shipmentNotes}</p>
            </div>
          )}
        </div>

        {/* Signatures */}
        <div className="grid grid-cols-2 gap-6 mt-8">
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 h-32 flex flex-col justify-end bg-gray-50">
            <p className="text-sm font-black text-gray-700 mb-2 text-center">توقيع المستلم</p>
            <div className="border-t-2 border-gray-400 pt-2">
              <p className="text-xs text-gray-500">الاسم: _________________</p>
              <p className="text-xs text-gray-500 mt-2">التاريخ: _________________</p>
            </div>
          </div>
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 h-32 flex flex-col justify-end bg-gray-50">
            <p className="text-sm font-black text-gray-700 mb-2 text-center">توقيع المندوب</p>
            <div className="border-t-2 border-gray-400 pt-2">
              <p className="text-xs text-gray-500">الاسم: _________________</p>
              <p className="text-xs text-gray-500 mt-2">التاريخ: _________________</p>
            </div>
          </div>
        </div>

        <div className="border-t-2 border-gray-200 mt-8 pt-4 text-center">
          <p className="font-black text-blue-900 text-lg">Target Express - Shipping & Logistics</p>
          <p className="text-xs text-gray-500 mt-1">targetexpress.secretofthedesert.com</p>
        </div>
      </div>

      <style>{`
        @media print {
          @page {
            size: A6 portrait;
            margin: 3mm;
          }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .no-print,
          nav,
          header,
          .navbar,
          .sidebar,
          .main-nav,
          .top-bar,
          button,
          .btn,
          [role="button"] {
            display: none !important;
          }

          ::-webkit-scrollbar {
            display: none !important;
          }

          .awb-label {
            position: relative !important;
            width: 99mm !important;
            max-width: 99mm !important;
            min-height: 0 !important;
            height: auto !important;
            margin: 0 auto !important;
            padding: 2mm !important;
            box-shadow: none !important;
            border: 0 !important;
            overflow: hidden !important;
            font-size: 7px !important;
          }

          .awb-label > div {
            margin-bottom: 1.5mm !important;
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }

          /* Header */
          .awb-label .border-b-4 {
            border-bottom-width: 1px !important;
            padding-bottom: 1mm !important;
            margin-bottom: 1.5mm !important;
          }

          .awb-label .border-b-4 img {
            width: 32px !important;
            height: 32px !important;
          }

          .awb-label .border-b-4 h1 {
            font-size: 16px !important;
            line-height: 1 !important;
          }

          .awb-label .border-b-4 p {
            font-size: 6px !important;
            line-height: 1 !important;
          }

          /* Tracking */
          .awb-label .border-b-4 .text-left {
            min-width: 40mm !important;
          }

          .awb-label .border-b-4 .text-left > div:first-child {
            font-size: 6px !important;
            margin-bottom: 1mm !important;
          }

          .awb-label .border-b-4 .text-left > div:last-child {
            padding: 1mm 2mm !important;
            border-width: 1px !important;
          }

          .awb-label .border-b-4 .text-left span {
            font-size: 11px !important;
            letter-spacing: 0 !important;
          }

          /* Barcode + QR */
          .awb-label .border-b-2 {
            border-bottom-width: 1px !important;
            padding-bottom: 1.5mm !important;
            margin-bottom: 1.5mm !important;
          }

          .awb-label .flex.items-center.justify-between.gap-6 {
            gap: 1.5mm !important;
          }

          .awb-label .bg-gray-50.p-4 {
            padding: 1mm !important;
          }

          .awb-label .bg-gray-50 {
            border-radius: 3px !important;
          }

          .awb-label .bg-gray-50 .text-xs {
            font-size: 6px !important;
            margin-bottom: 0.5mm !important;
          }

          /* Sender / Receiver */
          .awb-label .grid-cols-2 {
            gap: 1.5mm !important;
          }

          .awb-label .p-5 {
            padding: 1.5mm !important;
          }

          .awb-label .w-8.h-8 {
            width: 18px !important;
            height: 18px !important;
          }

          .awb-label .w-8.h-8 svg {
            width: 10px !important;
            height: 10px !important;
          }

          .awb-label .text-base {
            font-size: 9px !important;
            line-height: 1 !important;
          }

          .awb-label .space-y-2 > * + * {
            margin-top: 0.7mm !important;
          }

          .awb-label .space-y-2 svg {
            width: 10px !important;
            height: 10px !important;
          }

          .awb-label .space-y-2 {
            font-size: 7px !important;
          }

          /* Summary */
          .awb-label .grid-cols-3 {
            gap: 1mm !important;
          }

          .awb-label .grid-cols-3 > div {
            padding: 1.2mm !important;
          }

          .awb-label .grid-cols-3 svg {
            width: 15px !important;
            height: 15px !important;
            margin-bottom: 0.5mm !important;
          }

          .awb-label .grid-cols-3 .text-xs {
            font-size: 6px !important;
          }

          .awb-label .grid-cols-3 .text-lg {
            font-size: 10px !important;
          }

          /* Footer / notes */
          .awb-label .text-sm {
            font-size: 7px !important;
          }

          .awb-label .text-xs {
            font-size: 6px !important;
          }

          /* Signatures */
          .awb-label .h-32 {
            height: 12mm !important;
          }

          .awb-label .h-32 .text-sm {
            font-size: 6px !important;
          }

          .awb-label .h-32 .text-xs {
            font-size: 5px !important;
          }

          .awb-label .p-4 {
            padding: 1mm !important;
          }

          .awb-label .mt-8 {
            margin-top: 1.5mm !important;
          }

          .awb-label .pt-4 {
            padding-top: 1mm !important;
          }

          /* منع تقسيم البوليصة */
          .awb-label,
          .awb-label > div {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
        }
      `}</style>
    </div>
  );
}
